import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { request } from '../../api/client'
import type { AccountDetail, AccountListItem, DeletionOutcome } from './types'

/**
 * §6's table. `listAccounts` (service.ts) already says the list is small by
 * construction — one row per account, no pagination — so sorting, filtering
 * and searching all happen against this one fetch, client-side.
 */
export function useAdminUsers() {
  return useQuery({
    queryKey: ['admin', 'users'],
    queryFn: () => request<{ users: AccountListItem[] }>('/api/admin/users'),
  })
}

/**
 * §6's detail dialog — and, via its `deletion.scope`, the one place that
 * decides which of §6.0's two deletions applies. `DeleteAccountDialog`
 * reuses this same query rather than asking a second endpoint, so the
 * confirmation and the account it describes can never come from different
 * reads.
 */
export function useAdminUser(userId: string | null) {
  return useQuery({
    queryKey: ['admin', 'user', userId],
    queryFn: () => request<AccountDetail>(`/api/admin/users/${userId}`),
    enabled: userId !== null,
  })
}

/**
 * §6.1 / §7. The server re-checks `confirmEmail` against the account being
 * deleted regardless of what this sends — this call carries the operator's
 * typed confirmation, not a decision.
 */
export function useDeleteAdminUser() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, confirmEmail }: { id: string; confirmEmail: string }) =>
      request<{ deleted: DeletionOutcome }>(`/api/admin/users/${id}`, {
        method: 'DELETE',
        body: { confirmEmail },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'users'] })
      void queryClient.invalidateQueries({ queryKey: ['admin', 'stats'] })
    },
  })
}
